/**
 * Hook: User-Badge (Anzahl ungelesener Admin-Antworten)
 *
 * Leichtgewichtiger Hook für die Bubble — lädt initial die aktive
 * Konversation, hört auf WS-Events und aktualisiert den Counter live.
 *
 * Trennung vom useChatConversation:
 *   useChatConversation lädt zusätzlich alle Messages und macht Auto-Mark-Read.
 *   Der Bubble-Badge braucht nur die Zahl — viel weniger Overhead.
 *
 * Update-Trigger:
 *   - Initial-Load via REST (fetchActiveConversation)
 *   - WS message:new mit senderType='admin' → Counter++
 *   - WS message:read mit readByType='user' → Counter = 0 (User hat selbst gelesen)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchActiveConversation } from '../services/support/chatService';
import { useChatWebSocket } from './useChatWebSocket';

interface UseUserUnreadBadgeOptions {
  enabled?: boolean;
}

interface UseUserUnreadBadgeResult {
  count: number;
  refresh: () => Promise<void>;
}

export function useUserUnreadBadge(options: UseUserUnreadBadgeOptions = {}): UseUserUnreadBadgeResult {
  const enabled = options.enabled !== false;
  const [count, setCount] = useState(0);

  // Konversations-ID merken, damit WS-Events nur für die richtige Konversation
  // verarbeitet werden.
  const conversationIdRef = useRef<string | null>(null);

  const { subscribe, sendClientEvent } = useChatWebSocket({ enabled });

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    try {
      const conv = await fetchActiveConversation();
      conversationIdRef.current = conv.id;
      setCount(conv.unreadCountUser);
    } catch {
      /* still ok — Badge ist Best-Effort */
    }
  }, [enabled]);

  // Initial-Load
  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  // Live: neue Admin-Nachricht → Counter aus DB neu holen.
  // Bewusst KEIN lokales prev+1 — Multi-Tab/Reconnect-Szenarien führten
  // zu Driften (Counter zeigte 10 obwohl DB 2). Ein zusätzlicher REST-Call
  // pro empfangenem Admin-Event ist akzeptabel und hält uns konsistent
  // zur DB-Wahrheit.
  //
  // PLUS: delivered-ACK an Server senden, damit der Admin ✓✓-Häkchen sieht.
  // Dieser Hook ist auch dann aktiv, wenn der User die Bubble noch NICHT
  // geöffnet hat — daher der richtige Ort für "Frame ist im Browser".
  useEffect(() => {
    if (!enabled) return;
    const unsub = subscribe('message:new', (event) => {
      if (event.message.senderType !== 'admin') return;
      if (event.conversationId !== conversationIdRef.current) return;
      void refresh();
      sendClientEvent({ type: 'mark_delivered', conversationId: event.conversationId });
    });
    return unsub;
  }, [enabled, subscribe, refresh, sendClientEvent]);

  // Live: User selbst hat gelesen (über anderes Tab oder Auto-Mark-Read)
  useEffect(() => {
    if (!enabled) return;
    const unsub = subscribe('message:read', (event) => {
      if (event.readByType !== 'user') return;
      if (event.conversationId !== conversationIdRef.current) return;
      setCount(0);
    });
    return unsub;
  }, [enabled, subscribe]);

  return { count, refresh };
}
